import React, { useEffect, useRef, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../config/firebase.ts';
import { useNavigate, Link } from 'react-router-dom';
import PlayerName, { usePlayerDecorations } from '../Common/PlayerName.tsx';
import './DailyStreak.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.quizvs.com';
const QUESTION_TIME = 20; // seconds

interface DailyQuestion {
  index: number;
  id: number;
  question: string;
  options: string[];
  correct_answer_index: number; // 1-based
  imageUrl?: string; // URL de l'image pour la question
  difficulty: number;
  category: string;
  category_name?: string;
  category_emoji?: string;
}

// Fonction utilitaire pour mapper les catégories avec leurs émojis et noms
const getCategoryDisplay = (question: DailyQuestion) => {
  // Si les données de catégorie sont disponibles depuis l'API, les utiliser
  if (question.category_name && question.category_emoji) {
    return {
      name: question.category_name,
      emoji: question.category_emoji
    };
  }

  // Fallback avec mapping des catégories standards si les données API ne sont pas disponibles
  const categoryMap: Record<string, { name: string; emoji: string }> = {
    'arts': { name: 'Arts & Culture', emoji: '🎨' },
    'entertainment': { name: 'Divertissement', emoji: '🎬' },
    'geography': { name: 'Géographie', emoji: '🌍' },
    'history': { name: 'Histoire', emoji: '📚' },
    'science': { name: 'Sciences', emoji: '🔬' },
    'sports': { name: 'Sports', emoji: '⚽' },
    'mystery': { name: 'Mystère', emoji: '🔮' },
  };

  // Si c'est une catégorie standard
  const standardCategory = categoryMap[question.category.toLowerCase()];
  if (standardCategory) {
    return standardCategory;
  }

  // Si c'est une catégorie personnalisée (commence généralement par "custom_")
  if (question.category.startsWith('custom_')) {
    return { name: 'Catégorie Personnalisée', emoji: '👤' };
  }

  // Fallback pour les catégories inconnues
  return { name: 'Catégorie', emoji: '📝' };
};

const DailyStreakPage: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(1);
  const [score, setScore] = useState(0);
  const [question, setQuestion] = useState<DailyQuestion | null>(null);
  const [timer, setTimer] = useState(QUESTION_TIME);
  const [running, setRunning] = useState(false);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [gameEnded, setGameEnded] = useState(false);
  const [endReason, setEndReason] = useState<'wrong' | 'timeout' | 'cheat' | 'already_played' | null>(null);
  const [correctAnswer, setCorrectAnswer] = useState<string>('');
  const [nextDailyCountdown, setNextDailyCountdown] = useState<string>('');
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);
  const [todayScore, setTodayScore] = useState<number | null>(null);
  const [showAnswerFeedback, setShowAnswerFeedback] = useState(false);
  const [lastAnswerCorrect, setLastAnswerCorrect] = useState<boolean | null>(null);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const startTsRef = useRef<number>(0);
  const timerRef = useRef<any>(null);
  const countdownRef = useRef<any>(null);
  const questionRef = useRef<HTMLDivElement>(null);

  // Charger les effets de Noël pour les joueurs du leaderboard
  const leaderboardUserIds = leaderboard.map((r: any) => r.user_id);
  const { decorations } = usePlayerDecorations(leaderboardUserIds);

  // Auth gate
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) navigate('/login');
    });
    return () => unsub();
  }, [navigate]);

  // Anti-cheat: auto-submit if tab hidden while a question is active
  useEffect(() => {
    function onVisibility() {
      if (document.hidden && running) {
        handleGameOver('cheat');
      }
    }
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [running]);

  // Countdown to next daily challenge
  useEffect(() => {
    function updateCountdown() {
      const now = new Date();
      const tomorrow = new Date(now);
      tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
      tomorrow.setUTCHours(0, 0, 0, 0);
      
      const timeLeft = tomorrow.getTime() - now.getTime();
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
      
      setNextDailyCountdown(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    }

    if (gameEnded) {
      updateCountdown();
      countdownRef.current = setInterval(updateCountdown, 1000);
    }

    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [gameEnded]);

  // Auto-scroll vers la question sur mobile
  useEffect(() => {
    if (question && running && questionRef.current) {
      // Petit délai pour laisser le DOM se mettre à jour
      setTimeout(() => {
        questionRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });
      }, 100);
    }
  }, [question, running]);

  async function fetchStatus() {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/daily-streak/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setLeaderboard(data?.top || []);
      
      // Vérifier si le joueur a déjà joué aujourd'hui
      if (data?.mine) {
        setAlreadyPlayed(true);
        setTodayScore(data.mine.score);
        setGameEnded(true);
        setEndReason('already_played' as any); // Nouveau type de fin de partie
      }
    } catch (e) {
      // ignore
    }
  }

  async function fetchQuestion(i: number) {
    if (!user) return;
    setError(null);
    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_URL}/api/daily-streak/next`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ index: i, language: 'fr' })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Erreur inconnue' }));
        throw new Error(err.error || 'Échec de récupération de la question');
      }
      const q: DailyQuestion = await res.json();
      setQuestion(q);
      setTimer(QUESTION_TIME);
      setRunning(true);
    } catch (e: any) {
      setError(e?.message || 'Erreur');
      setQuestion(null);
      setRunning(false);
    }
  }

  async function submitScore(finalScore: number) {
    if (!user) return;
    try {
      const token = await user.getIdToken();
      // Calculer la durée avec un minimum de 1 seconde
      let durationSec = startTsRef.current > 0 ? Math.floor((Date.now() - startTsRef.current) / 1000) : 0;
      // Garantir un minimum de 1 seconde si le jeu a vraiment été joué
      if (durationSec === 0 && startTsRef.current > 0) {
        durationSec = 1;
      }
      durationSec = Math.max(1, durationSec);
      await fetch(`${API_URL}/api/daily-streak/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ score: finalScore, durationSec })
      });
      await fetchStatus();
    } catch (e) {
      // ignore
    }
  }

  function handleGameOver(reason: 'wrong' | 'timeout' | 'cheat' | 'already_played' = 'wrong', wrongAnswer?: string) {
    setRunning(false);
    setGameEnded(true);
    setEndReason(reason);
    
    // Get the correct answer text if we have a question and it's not already played
    if (question && reason === 'wrong') {
      const correctIndex = question.correct_answer_index - 1; // Convert from 1-based to 0-based
      setCorrectAnswer(question.options[correctIndex] || '');
    }
    
    // Ne soumettre le score que si ce n'est pas déjà joué
    if (reason !== 'already_played') {
      submitScore(score);
    }
  }

  // Fetch status first to check if already played
  useEffect(() => {
    if (!loading && user) {
      fetchStatus();
    }
  }, [loading, user]);

  // Start / timer - only if not already played
  useEffect(() => {
    if (!loading && user && !alreadyPlayed) {
      startTsRef.current = Date.now();
      fetchQuestion(1);
    }
  }, [loading, user, alreadyPlayed]);

  useEffect(() => {
    if (!running) return;
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setTimer((t) => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          handleGameOver('timeout');
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [running]);

  function handleAnswer(choiceIndex: number) {
    if (!question || showAnswerFeedback) return; // Empêcher les clics multiples pendant le feedback
    
    setSelectedAnswer(choiceIndex);
    setRunning(false); // Arrêter le timer
    clearInterval(timerRef.current);
    
    const isCorrect = (choiceIndex + 1) === Number(question.correct_answer_index || 0);
    setLastAnswerCorrect(isCorrect);
    setShowAnswerFeedback(true);
    
    // Afficher le feedback pendant 2 secondes avant de continuer
    setTimeout(() => {
      setShowAnswerFeedback(false);
      setSelectedAnswer(null);
      setLastAnswerCorrect(null);
      
      if (!isCorrect) {
        // Si mauvaise réponse, révéler la bonne réponse et terminer le jeu
        const correctIndex = question.correct_answer_index - 1; // Convert from 1-based to 0-based
        setCorrectAnswer(question.options[correctIndex] || '');
        handleGameOver('wrong');
      } else {
        // Si bonne réponse, continuer
        setScore((s) => s + 1);
        const next = index + 1;
        setIndex(next);
        // Réinitialiser explicitement pour iOS
        setQuestion(null);
        fetchQuestion(next);
      }
    }, 2000);
  }

  if (loading) return <div style={{ padding: 20 }}>Chargement…</div>;
  if (!user) return null;

  return (
    <div className="daily-streak-container">
      <div className="daily-streak-header">
        <h2 className="daily-streak-title">Daily Streak</h2>
        <div className="daily-streak-nav">
          <Link to="/daily/leaderboard" className="daily-streak-nav-link">
            📋 Classements
          </Link>
          <button onClick={() => navigate('/')} className="daily-streak-back-btn">
            ← Retour
          </button>
        </div>
      </div>
      
      <div className="daily-streak-description">
        <p>
          🎯 <strong>Défi quotidien</strong> : Une seule erreur met fin à la série • 
          ⏰ 20 secondes par question • 
          🚫 Changer d'onglet met fin à la partie
        </p>
      </div>

      <div className="daily-streak-content">
        <div className="daily-streak-game">
          {!alreadyPlayed && (
            <div className="daily-streak-stats">
              <div className="daily-streak-stat">
                <span className="daily-streak-stat-value">{score}</span>
                <span className="daily-streak-stat-label">Score</span>
              </div>
              <div className="daily-streak-stat">
                <span className="daily-streak-stat-value">#{index}</span>
                <span className="daily-streak-stat-label">Question</span>
              </div>
            </div>
          )}

          {!alreadyPlayed && running && (
            <div className="timer-progress-container">
              <div className="timer-progress-header">
                <span className="timer-progress-label">Temps restant</span>
                <span className={`timer-progress-value ${timer <= 5 ? 'warning' : ''}`}>
                  ⏱️ {timer}s
                </span>
              </div>
              <div className="timer-progress-bar">
                <div 
                  className={`timer-progress-fill ${timer <= 5 ? 'warning' : ''}`}
                  style={{ width: `${(timer / QUESTION_TIME) * 100}%` }}
                ></div>
              </div>
            </div>
          )}

          {error && !alreadyPlayed && (
            <div className="daily-streak-error">
              ❌ {error}
            </div>
          )}

          {question && !alreadyPlayed ? (
            <div ref={questionRef} className="daily-streak-question-card">
              <div className="daily-streak-question-meta">
                <span className="daily-streak-question-category">
                  {(() => {
                    const categoryDisplay = getCategoryDisplay(question);
                    return `${categoryDisplay.emoji} ${categoryDisplay.name}`;
                  })()}
                </span>
                <div className="daily-streak-question-difficulty">
                  {Array.from({ length: question.difficulty }, (_, i) => (
                    <span key={i} className="difficulty-star">★</span>
                  ))}
                </div>
              </div>
              
              <div className="daily-streak-question-text">
                {question.question}
              </div>

              {/* Affichage de l'image si présente */}
              {question.imageUrl && (
                <div className="daily-streak-question-image">
                  <img
                    src={question.imageUrl}
                    alt="Question illustration"
                    style={{
                      maxWidth: '300px',
                      maxHeight: '200px',
                      borderRadius: '8px',
                      objectFit: 'contain',
                      border: '2px solid var(--accent-color)',
                      margin: '16px auto',
                      display: 'block',
                    }}
                    onError={(e) => {
                      console.warn('❌ [Daily Streak] Image failed to load:', question.imageUrl);
                      e.currentTarget.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('✅ [Daily Streak] Image loaded successfully:', question.imageUrl);
                    }}
                  />
                </div>
              )}
              
              <div className="daily-streak-options">
                {question.options?.map((opt: string, i: number) => {
                  let buttonClass = "daily-streak-option";
                  
                  if (showAnswerFeedback) {
                    const correctIndex = question.correct_answer_index - 1;
                    if (i === correctIndex) {
                      buttonClass += " correct-answer";
                    } else if (i === selectedAnswer) {
                      buttonClass += " wrong-answer";
                    }
                  }
                  
                  return (
                    <button
                      key={`${question.id}-${index}-${i}-${opt.substring(0, 10)}`}
                      onClick={() => handleAnswer(i)}
                      className={buttonClass}
                      disabled={!running || showAnswerFeedback}
                    >
                      <span className="option-letter">{String.fromCharCode(65 + i)}</span>
                      {opt}
                      {showAnswerFeedback && i === question.correct_answer_index - 1 && (
                        <span className="feedback-icon">✓</span>
                      )}
                      {showAnswerFeedback && i === selectedAnswer && i !== question.correct_answer_index - 1 && (
                        <span className="feedback-icon">✗</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (!gameEnded && !alreadyPlayed) ? (
            <div className="daily-streak-loading">
              ⚡ Chargement de la question...
            </div>
          ) : null}

          {gameEnded && (
            <div className="daily-streak-game-over">
              <div className="game-over-content">
                <div className="game-over-icon">
                  {endReason === 'wrong' && '💥'}
                  {endReason === 'timeout' && '⏰'}
                  {endReason === 'cheat' && '🚫'}
                  {endReason === 'already_played' && '✅'}
                </div>
                
                <h3 className="game-over-title">
                  {endReason === 'wrong' && 'Mauvaise réponse !'}
                  {endReason === 'timeout' && 'Temps écoulé !'}
                  {endReason === 'cheat' && 'Tentative de triche détectée !'}
                  {endReason === 'already_played' && 'Déjà joué aujourd\'hui !'}
                </h3>
                
                <div className="game-over-stats">
                  <div className="final-score">
                    <span className="score-label">
                      {endReason === 'already_played' ? 'Score d\'aujourd\'hui' : 'Score final'}
                    </span>
                    <span className="score-value">
                      {endReason === 'already_played' ? (todayScore || 0) : score}
                    </span>
                    <span className="score-questions">
                      question{(endReason === 'already_played' ? (todayScore || 0) : score) > 1 ? 's' : ''} réussie{(endReason === 'already_played' ? (todayScore || 0) : score) > 1 ? 's' : ''}
                    </span>
                  </div>
                  
                  {endReason === 'wrong' && correctAnswer && (
                    <div className="correct-answer">
                      <span className="correct-label">La bonne réponse était :</span>
                      <span className="correct-value">✅ {correctAnswer}</span>
                    </div>
                  )}
                </div>

                <div className="next-challenge">
                  <div className="countdown-container">
                    <h4>⏳ Prochaine session Daily Streak dans :</h4>
                    <div className="countdown-timer">
                      {nextDailyCountdown}
                    </div>
                    <p className="countdown-hint">
                      Un nouveau défi vous attend demain avec de nouvelles questions !
                    </p>
                  </div>
                </div>

                <div className="game-over-actions">
                  <button 
                    onClick={() => navigate('/daily/leaderboard')} 
                    className="daily-streak-leaderboard-btn"
                  >
                    📊 Voir le classement complet
                  </button>
                </div>
              </div>
            </div>
          )}

          {!running && !gameEnded && question && (
            <div className="daily-streak-loading">
              ⚡ En attente...
            </div>
          )}
        </div>

        <div className="daily-streak-leaderboard">
          <h3>🏆 Top du jour</h3>
          {leaderboard?.length === 0 ? (
            <div className="leaderboard-empty">
              🎭 Soyez le premier à marquer des points !
            </div>
          ) : (
            <ol className="leaderboard-list">
              {leaderboard.map((r: any, i: number) => (
                <li 
                  key={i} 
                  className={`leaderboard-item ${i < 3 ? 'top-three' : ''}`}
                >
                  <span className="leaderboard-rank">
                    {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`}
                  </span>
                  <PlayerName 
                    userId={r.user_id}
                    userName={r.user_name || r.user_id}
                    decoration={decorations[r.user_id]}
                    className="leaderboard-name"
                  />
                  <span className="leaderboard-score">
                    {r.score} pts • {r.duration_sec}s
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default DailyStreakPage;
